import React, { useCallback, useMemo, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import { Grid } from '@mui/material';
import { GridColDef, GridRowsProp } from '@mui/x-data-grid-pro';
import { FormikHelpers, FormikProps } from 'formik';
import * as Yup from 'yup';

import { TipoColunaDinamicaAutoComplete } from 'components/autocompletes/TipoColunaDinamicaAutoComplete';
import { CissTextField } from 'components/fields';
import { Row } from 'components/form';
import { PanelGrid } from 'components/grid';
import { FullLoading } from 'components/loading';
import { ConfirmModal } from 'components/modal';
import { Block, FormPage } from 'components/page';
import { SubTitle } from 'components/text';
import { useFormContext } from 'context/FormContext';
import { usePermissionsContext } from 'context/PermissionsContext';
import { useFormMutation } from 'mutations/useFormMutation';
import { HeaderColunasOpcoes } from 'pages/cadastro/colunas-dinamicas/components/atoms/HeaderColunasOpcoes';
import { ListColunasOpcoes } from 'pages/cadastro/colunas-dinamicas/components/atoms/ListColunasOpcoes';
import { useColunasDinamicasContext } from 'pages/cadastro/colunas-dinamicas/contexts/ColunasDinamicasContext';
import { useMutateColunasDinamicas } from 'pages/cadastro/colunas-dinamicas/hooks/useMutateColunasDinamicas';
import { ColunaDinamicaProps } from 'pages/cadastro/loja/types';

const mapContentToInitialValues = (contentData: ColunaDinamicaProps[]) => {
    if (Array.isArray(contentData)) {
        return contentData.map((config) => ({
            ...config,
        }));
    }

    return contentData;
};

const url = 'gestao/configuracaopropriedade/';

export function SecaoColunasDinamicasForm(): JSX.Element {
    const { isLoading } = useFormMutation();
    const { idSecao } = useParams();
    const { setContent, content } = useFormContext();
    const { onSubmitForm } = useMutateColunasDinamicas();
    const { openModalColunasDinamicas, setOpenModalColunasDinamicas } = useColunasDinamicasContext();

    const initialFormValues: ColunaDinamicaProps = {
        dsConfiguracao: '',
        nrSequencia: '',
        dsChave: '',
        fgReordena: false,
        fgAtivo: false,
        tipoConfiguracaoPropriedade: {
            idTipoConfiguracaoPropriedade: 0,
            dsTipoConfiguracaoPropriedade: '',
        },
        configuracaoPropriedadeGrupoExibicao: {
            idConfiguracaoPropriedadeGrupoExibicao: idSecao,
        },
        tipoConfiguracaoPropriedadeOpcao: false,
    };

    const validationSchemaLoja = useMemo(
        () =>
            Yup.object().shape({
                dsChave: Yup.string().required('A chave é obrigatória').nullable(),
                dsConfiguracao: Yup.string().required('A descrição é obrigatória').nullable(),
                nrSequencia: Yup.string().required('A sequência é obrigatória').nullable(),
                tipoConfiguracaoPropriedade: Yup.object({
                    idTipoConfiguracaoPropriedade: Yup.string().required('A sequência é obrigatória').nullable(),
                    dsTipoConfiguracaoPropriedade: Yup.string().required('o Tipo é obrigatório').nullable(),
                }),
                configuracaoPropriedadeOpcaoList: Yup.array()
                    .of(
                        Yup.object().shape({
                            idConfiguracaoPropriedadeOpcao: Yup.string().nullable(),
                            dsConfiguracaoPropriedadeOpcao: Yup.string().nullable(),
                        }),
                    )
                    .nullable(),
                tipoConfiguracaoPropriedadeOpcao: Yup.boolean().nullable(),
            }),
        [],
    );

    return (
        <FormPage
            title={'Editar/Inserir Coluna'}
            values={initialFormValues}
            url={url}
            onSubmit={onSubmitForm}
            validationSchema={validationSchemaLoja}
            mapContentToInitialValues={mapContentToInitialValues}
            fgAtivo
        >
            {(formik: FormikProps<ColunaDinamicaProps>) => {
                return (
                    <>
                        <FullLoading loading={isLoading} />

                        <Block>
                            <Row>
                                <Grid item flex={1}>
                                    <CissTextField
                                        label="Chave"
                                        name="dsChave"
                                        value={formik.values.dsChave}
                                        error={formik.errors.dsChave}
                                        onChange={(e) => formik.setFieldValue('dsChave', e.target.value)}
                                    />
                                </Grid>

                                <Grid item flex={3}>
                                    <CissTextField
                                        label="Descrição da coluna"
                                        name="dsConfiguracao"
                                        value={formik.values.dsConfiguracao}
                                        error={formik.errors.dsConfiguracao}
                                        onChange={(e) => formik.setFieldValue('dsConfiguracao', e.target.value)}
                                    />
                                </Grid>

                                <Grid item flex={1}>
                                    <CissTextField
                                        label="Ordem de apresentação"
                                        name="nrSequencia"
                                        value={formik.values.nrSequencia}
                                        error={formik.errors.nrSequencia}
                                        onChange={(e) => formik.setFieldValue('nrSequencia', e.target.value)}
                                    />
                                </Grid>

                                <Grid item flex={1.5}>
                                    <TipoColunaDinamicaAutoComplete
                                        label="Tipo de campo"
                                        value={formik.values?.tipoConfiguracaoPropriedade}
                                        onChange={(e, value) => {
                                            value.idTipoConfiguracaoPropriedade == 4 &&
                                                formik.setFieldValue('tipoConfiguracaoPropriedadeOpcao', true);
                                            formik.setFieldValue('tipoConfiguracaoPropriedade', value);
                                            setContent({ ...formik.values, tipoConfiguracaoPropriedade: value });
                                        }}
                                    />
                                </Grid>
                            </Row>
                        </Block>

                        {content?.tipoConfiguracaoPropriedade?.idTipoConfiguracaoPropriedade === 4 && <ListColunasOpcoes />}

                        <ConfirmModal
                            open={openModalColunasDinamicas}
                            setOpen={setOpenModalColunasDinamicas}
                            confirm={() => {
                                onSubmitForm({ ...formik.values, fgReordena: true }, formik);
                                setOpenModalColunasDinamicas((prevState) => !prevState);
                            }}
                            text="Já existe uma coluna com essa ordem, deseja reordenar mesmo assim?"
                        />
                    </>
                );
            }}
        </FormPage>
    );
}
